import * as React from 'react';
import {useDispatch} from 'react-redux';

import {Button} from 'shells/button/button';
import type {ButtonSize, ButtonVariant} from 'shells/button/types';
import {colors} from 'shells/colors/colors';
import {SmsIcon} from 'shells/icon/svg/sms-icon';
// @ts-expect-error - imported from .js/.jsx file
import {UpgradeArrowIcon} from 'shells/icon/svg/upgrade-arrow-icon';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {SmsFeatureGateModal} from '../../../limits/feature-gates/sms-feature-gate-modal';
import {SmsFeatureGateModalWithLicense} from '../../../limits/feature-gates/sms-feature-gate-modal-with-license';
import {OpenableModals} from '../../../openable-modals';
import * as actions from '../../../openable-modals/openable-modals-actions';

type Props = {
    entityId: string;
    entityPageName: string | null | undefined;
    openedFromPhoneNumber?: string;
    buttonVariant?: ButtonVariant;
    buttonSize?: ButtonSize;
    buttonText?: string;
    buttonIcon?: React.ComponentType<any>;
    shouldClickPreventDefault?: boolean;
};

export function SmsSidebarButton(props: Props) {
    const {
        entityId,
        entityPageName,
        buttonIcon = SmsIconComponent,
        buttonVariant = 'primary',
    } = props;

    const [isFeatureGateModalOpen, setIsFeatureGateModalOpen] = React.useState(false);

    const buttonRef = React.useRef(null);

    const {user} = useGetSessionUser();
    const dispatch = useDispatch();

    const canUseSms = user && user.permissions.canUseSms;
    const featureName = NutClientConfig.hasSms ? 'SMSWithLicense' : 'SMS';

    const handleOpenModal = React.useCallback(
        (fromNumber?: string) => {
            if (canUseSms) {
                dispatch(
                    actions.openModal(OpenableModals.SMS_SIDEBAR, {
                        openedFromPhoneNumber: fromNumber,
                        entityId,
                        openedFromEntityName: entityPageName,
                    })
                );
            } else {
                setIsFeatureGateModalOpen(true);
            }
        },
        [canUseSms, entityId, entityPageName, dispatch]
    );

    const handleOpenModalFromPhoneNumber = React.useCallback(
        (
            e: any,
            options: {
                openedFromPhoneNumber: string;
            }
        ) => {
            handleOpenModal(options.openedFromPhoneNumber);
        },
        [handleOpenModal]
    );

    React.useEffect(() => {
        $(window).on(Peep.events.openInboxSidebarFromPhoneNumber, handleOpenModalFromPhoneNumber);

        return () => {
            $(window).off(
                Peep.events.openInboxSidebarFromPhoneNumber,
                handleOpenModalFromPhoneNumber
            );
        };
    }, [handleOpenModalFromPhoneNumber]);

    const FeatureGateComponent =
        featureName === 'SMS' ? SmsFeatureGateModal : SmsFeatureGateModalWithLicense;

    return (
        <>
            <Button
                icon={canUseSms ? buttonIcon : UpgradeIcon}
                size={props.buttonSize || 'big'}
                variant={canUseSms ? buttonVariant : 'upgrade'}
                onClick={(e) => {
                    if (props.shouldClickPreventDefault) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                    handleOpenModal(props.openedFromPhoneNumber);
                }}
                getButtonRef={buttonRef}
            >
                {props.buttonText || 'Send a text'}
            </Button>
            <FeatureGateComponent
                isOpen={isFeatureGateModalOpen}
                onClose={() => {
                    setIsFeatureGateModalOpen(false);
                }}
            />
        </>
    );
}

function UpgradeIcon() {
    return <UpgradeArrowIcon size={18} wrapWithDiv={false} color={colors.blue} />;
}

function SmsIconComponent() {
    // @ts-expect-error
    return <SmsIcon size={18} wrapWithDiv={false} color={colors.white} />;
}
